import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { BackgroundMusicTrack } from '../types/database';

interface MusicState {
  // Current track
  currentTrack: BackgroundMusicTrack | null;
  tracks: BackgroundMusicTrack[];
  
  // Player state
  isPlaying: boolean;
  volume: number;
  isMuted: boolean;
  currentTime: number;
  duration: number;
  
  // UI state
  isPanelOpen: boolean;
  activeTab: 'library' | 'generate' | 'search' | 'upload';
  
  // Loading states
  isLoading: boolean;
  isGenerating: boolean;
  error: string | null;
  
  // Actions
  setCurrentTrack: (track: BackgroundMusicTrack | null) => void;
  setTracks: (tracks: BackgroundMusicTrack[]) => void;
  addTrack: (track: BackgroundMusicTrack) => void;
  removeTrack: (trackId: string) => void;
  updateTrack: (trackId: string, updates: Partial<BackgroundMusicTrack>) => void;
  reorderTracks: (trackIds: string[]) => void;
  
  setIsPlaying: (playing: boolean) => void;
  setVolume: (volume: number) => void;
  setIsMuted: (muted: boolean) => void;
  setCurrentTime: (time: number) => void;
  setDuration: (duration: number) => void;
  
  setIsPanelOpen: (open: boolean) => void;
  setActiveTab: (tab: 'library' | 'generate' | 'search' | 'upload') => void;
  
  setIsLoading: (loading: boolean) => void;
  setIsGenerating: (generating: boolean) => void;
  setError: (error: string | null) => void;
  
  // Playback controls
  play: () => void;
  pause: () => void;
  toggleMute: () => void;
  seek: (time: number) => void;
  
  // Reset
  resetMusic: () => void;
}

const initialState: Omit<MusicState, keyof {
  setCurrentTrack: unknown;
  setTracks: unknown;
  addTrack: unknown;
  removeTrack: unknown;
  updateTrack: unknown;
  reorderTracks: unknown;
  setIsPlaying: unknown;
  setVolume: unknown;
  setIsMuted: unknown;
  setCurrentTime: unknown;
  setDuration: unknown;
  setIsPanelOpen: unknown;
  setActiveTab: unknown;
  setIsLoading: unknown;
  setIsGenerating: unknown;
  setError: unknown;
  play: unknown;
  pause: unknown;
  toggleMute: unknown;
  seek: unknown;
  resetMusic: unknown;
}> = {
  currentTrack: null,
  tracks: [],
  
  isPlaying: false,
  volume: 0.7,
  isMuted: false,
  currentTime: 0,
  duration: 0,
  
  isPanelOpen: false,
  activeTab: 'library' as const,
  
  isLoading: false,
  isGenerating: false,
  error: null,
};

export const useMusicStore = create<MusicState>()(
  persist(
    (set, get) => ({
      ...initialState,
      
      setCurrentTrack: (track) => set({ currentTrack: track }),
      
      setTracks: (tracks) => set({ tracks }),
      
      addTrack: (track) => {
        set((state) => ({
          tracks: [...state.tracks, track],
        }));
      },
      
      removeTrack: (trackId) => {
        set((state) => ({
          tracks: state.tracks.filter((t) => t.id !== trackId),
          currentTrack: state.currentTrack?.id === trackId ? null : state.currentTrack,
        }));
      },
      
      updateTrack: (trackId, updates) => {
        set((state) => ({
          tracks: state.tracks.map((track) =>
            track.id === trackId ? { ...track, ...updates } : track
          ),
          currentTrack: state.currentTrack?.id === trackId
            ? { ...state.currentTrack, ...updates }
            : state.currentTrack,
        }));
      },
      
      reorderTracks: (trackIds) => {
        set((state) => {
          const trackMap = new Map(state.tracks.map((t) => [t.id, t]));
          return {
            tracks: trackIds
              .map((id) => trackMap.get(id))
              .filter((t): t is BackgroundMusicTrack => !!t)
              .map((track, index) => ({ ...track, order_index: index })),
          };
        });
      },
      
      setIsPlaying: (isPlaying) => set({ isPlaying }),
      
      setVolume: (volume) => {
        set({ volume: Math.max(0, Math.min(1, volume)) });
      },
      
      setIsMuted: (isMuted) => set({ isMuted }),
      
      setCurrentTime: (currentTime) => set({ currentTime }),
      
      setDuration: (duration) => set({ duration }),
      
      setIsPanelOpen: (isPanelOpen) => set({ isPanelOpen }),
      
      setActiveTab: (activeTab) => set({ activeTab }),
      
      setIsLoading: (isLoading) => set({ isLoading }),
      
      setIsGenerating: (isGenerating) => set({ isGenerating }),
      
      setError: (error) => set({ error }),
      
      play: () => set({ isPlaying: true }),
      
      pause: () => set({ isPlaying: false }),
      
      toggleMute: () => set((state) => ({ isMuted: !state.isMuted })),
      
      seek: (time) => {
        set({ currentTime: Math.max(0, Math.min(get().duration, time)) });
      },
      
      resetMusic: () => set(initialState),
    }),
    {
      name: 'music-storage',
      partialize: (state) => ({
        tracks: state.tracks,
        volume: state.volume,
        isMuted: state.isMuted,
      }),
    }
  )
);
